/**
 * Chart palette (Phase 3, §6.4) — SPINE. The single source of truth for every
 * colour a chart draws. Validated PASS (colourblind-safe, ≥3:1 vs adjacent) on
 * light `#ffffff` and dark `#0f172a` surfaces by the FE designer.
 *
 * Binding rules:
 *  - Categorical slots are a FIXED order and are NEVER cycled; slot 9+ folds to
 *    the muted "Andere" grey.
 *  - Colour follows the ENTITY: a bucket's own `color` wins; the categorical
 *    palette is only a chart-only fallback for colourless buckets — so dropping
 *    a series never repaints the survivors.
 *  - Semantic colours (Einnahmen green / Ausgaben red / net / net-worth sky)
 *    and the status ramp are RESERVED and never used as a data series.
 */

export type ChartMode = 'light' | 'dark';

/** Fixed categorical slots for light surfaces (never cycled). */
export const CATEGORICAL_LIGHT = [
  '#2a78d6',
  '#1baf7a',
  '#eda100',
  '#008300',
  '#4a3aa7',
  '#e34948',
  '#e87ba4',
  '#eb6834',
] as const;

/** Fixed categorical slots for dark surfaces (same hue order, retuned for `#0f172a`). */
export const CATEGORICAL_DARK = [
  '#3987e5',
  '#199e70',
  '#c98500',
  '#008300',
  '#9085e9',
  '#e66767',
  '#d55181',
  '#d95926',
] as const;

/** Muted grey for the folded "Andere" slot (slot 9+). */
export const OTHER_COLOR: Record<ChartMode, string> = {
  light: '#94a3b8',
  dark: '#64748b',
};

/** Reserved semantic colours — never assigned to a categorical data series. */
export const SEMANTIC: Record<'income' | 'expense' | 'net' | 'netWorth', Record<ChartMode, string>> =
  {
    income: { light: '#008300', dark: '#22c55e' },
    expense: { light: '#e34948', dark: '#f87171' },
    net: { light: '#334155', dark: '#e2e8f0' },
    netWorth: { light: '#0284c7', dark: '#38bdf8' },
  };

/** The categorical colour for a fixed slot; slot 9+ (or out of range) → "Andere". */
export function categoricalColor(slotIndex: number, mode: ChartMode): string {
  const palette = mode === 'dark' ? CATEGORICAL_DARK : CATEGORICAL_LIGHT;
  if (!Number.isInteger(slotIndex) || slotIndex < 0 || slotIndex >= palette.length) {
    return OTHER_COLOR[mode];
  }
  return palette[slotIndex];
}

/** Entity-first colour assignment (§6.4): a bucket's own colour wins; otherwise
 * the categorical palette at the given fixed slot. */
export function getBucketColor(
  bucket: { color?: string | null } | null | undefined,
  mode: ChartMode,
  slotIndex: number,
): string {
  if (bucket?.color) return bucket.color;
  return categoricalColor(slotIndex, mode);
}

/** A reserved semantic colour resolved for the active theme. */
export function semanticColor(key: keyof typeof SEMANTIC, mode: ChartMode): string {
  return SEMANTIC[key][mode];
}
